/**
 * HVP Cache Manager - statistics and size estimation
 *
 * Hit-rate/statistics reporting, entry metadata and the byte-size
 * estimators used by getStats().
 *
 * Split out of cache-manager.js (350-line rule): the methods below moved
 * VERBATIM and are installed on HVPCacheManager.prototype by
 * cache-manager.js, so `this` is the same instance and behavior is identical.
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

export const CacheStatsMethods = {
    /**
     * Get cache statistics
     *
     * @returns {Object} Statistics
     */
    getStats() {
        const hitRate = this.stats.hits + this.stats.misses > 0
            ? (this.stats.hits / (this.stats.hits + this.stats.misses) * 100).toFixed(2)
            : 0;

        return {
            ...this.stats,
            size: this.cache.size,
            maxSize: this.maxSize,
            hitRate: parseFloat(hitRate),
            memoryEstimate: this.estimateTotalSize()
        };
    },

    /**
     * Estimate size of cached data
     *
     * @param {*} value - Value to estimate
     * @returns {number} Estimated size in bytes
     */
    estimateSize(value) {
        if (value === null || value === undefined) return 0;

        const type = typeof value;

        switch (type) {
            case 'string':
                return value.length * 2; // UTF-16

            case 'number':
            case 'boolean':
                return 8;

            case 'object':
                if (Array.isArray(value)) {
                    return value.reduce((sum, item) => sum + this.estimateSize(item), 0);
                }
                return JSON.stringify(value).length * 2;

            default:
                return 8;
        }
    },

    /**
     * Estimate total cache size
     *
     * @returns {string} Human-readable size
     */
    estimateTotalSize() {
        let total = 0;

        for (const meta of this.metadata.values()) {
            total += meta.size;
        }

        // Convert to KB
        const kb = total / 1024;
        if (kb < 1024) {
            return `${kb.toFixed(2)} KB`;
        }

        // Convert to MB
        const mb = kb / 1024;
        return `${mb.toFixed(2)} MB`;
    },

    /**
     * Get cache entry metadata
     *
     * @param {string} key - Cache key
     * @returns {Object|null} Metadata
     */
    getMetadata(key) {
        return this.metadata.get(key) || null;
    },

    /**
     * Export cache data
     *
     * @returns {Object} Cache data
     */
    export() {
        const data = {};

        for (const [key, value] of this.cache.entries()) {
            const meta = this.metadata.get(key);
            data[key] = {
                value,
                metadata: meta
            };
        }

        return {
            data,
            stats: this.getStats(),
            timestamp: Date.now()
        };
    },
};

export default CacheStatsMethods;
